import { useMemo, useRef, useState } from "react";
import moment, { type unitOfTime } from "moment";
import { useLocation, useParams, useSearch } from "@tanstack/react-router";
import type Highcharts from "highcharts/es-modules/masters/highcharts.src.js";
import type { SeriesLegendItemClickEventObject, YAxisOptions } from "highcharts/highcharts.src";
import { Chart, type HighchartsReactRefObject } from "@highcharts/react";
import { trellisPalette } from "../analytics/palettes";
import { css } from "../../../../styled-system/css";
import { AnnotationPopover, supportedOrgLevelAnnotationPaths } from "../growth/AnnotationPopover";
import { useQuery } from "@tanstack/react-query";
import { keepPreviousData } from "@tanstack/react-query";

import { useGetAnnotations } from "../../../modules/annotations/api/useGetAnnotations";
import { useDates } from "../../../dates/DatesContext";
import { useAppSelector } from "../../../redux/hooks";
import { metricNameSelector, type MetricConfig } from "../../../modules/merchandising/utils/metrics";
import { formatMetricValue } from "../../../utils/formatNumber";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "../../../i18n/useDateFormatter";

declare module "highcharts/highcharts.src" {
  interface XAxisPlotLinesOptions {
    fillOpacity?: number;
  }

  // in TS these are normally only available for specific chart types, but at runtime specifying it for all series works
  interface PlotSeriesOptions {
    pointPadding?: number;
    groupPadding?: number;
  }

  interface Chart {
    isDirtyLegend?: boolean;
  }

  interface LegendItemObject {
    textStr?: string;
  }
}

const ANNOTATION_MARKER_RADIUS = 6;

interface Props {
  data?: { start_date: string }[];
  metrics: Array<MetricConfig & { isPrev?: boolean }>;
  size?: string;
  mobileStyles?: unknown;
  selectedMetrics: Array<string | number>;
  loading?: boolean;
  showAnnotations?: boolean;
  group?: string;
  handleLegendChange?: (e: SeriesLegendItemClickEventObject) => boolean;
  selectedCategory?: string | number | null;
  selectedAd?: string | number | null;
  selectedProduct?: string | number | null;
  highlightDate?: string | null;
  cellHighlightKey?: string | null;
  cellHighlightDates?: string[] | null;
  overrideHeight?: number | null;
  marketPlace?: string;
}

const MultiMetricChart = ({
  data,
  metrics,
  size,
  mobileStyles,
  selectedMetrics,
  loading = false,
  showAnnotations = true,
  group = "day",
  handleLegendChange,
  selectedCategory = null,
  selectedAd = null,
  selectedProduct = null,
  highlightDate = null,
  cellHighlightKey = null,
  cellHighlightDates = null,
  overrideHeight = null,
  marketPlace: marketPlaceOverride,
}: Props) => {
  const height = overrideHeight || (size === "small" || mobileStyles ? 215 : 315);
  const { category: categoryParam = null } = useParams({ strict: false });
  const categoryIdParam = useSearch({ strict: false, select: (search) => search.categoryId });
  const pathname = useLocation({ select: (location) => location.pathname });
  let marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  marketPlace = marketPlaceOverride ?? marketPlace;
  const { t } = useTranslation(["merchandising", "metrics"]);
  const mediumFmt = useDateFormatter("medium");
  const monthYearFmt = useDateFormatter("monthYear");
  const monthDayFmt = useDateFormatter("monthDay");

  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Reset selectedDate when product or ad selection changes
  const productAdKey = `${selectedProduct ?? "none"}-${selectedAd ?? "none"}`;
  const prevProductAdKey = useRef(productAdKey);
  if (prevProductAdKey.current !== productAdKey) {
    prevProductAdKey.current = productAdKey;
    if (selectedDate !== null) {
      setSelectedDate(null);
    }
  }

  // By default, annotations assumed depending on subscription tier, unless explicitly disabled in showAnnotations prop (i.e. the organization summary chart)
  const annotationsEnabled = showAnnotations !== false;

  const { group: contextGroup } = useDates();
  const chartRef = useRef<HighchartsReactRefObject>(null);
  // The annotation popover's trigger, set when the chart loads. The popover re-measures when its trigger resizes, and
  // Highcharts resizes its own container only after laying the chart out again (100ms after its wrapper), so the popover
  // follows the marker.
  const chartContainerRef = useRef<HTMLElement | null>(null);

  const category = useMemo(() => {
    return categoryParam || categoryIdParam || selectedCategory || "all";
  }, [categoryParam, categoryIdParam, selectedCategory]);

  const [hasData, dates, series] = useMemo(() => {
    const dates = data?.map((d) => d.start_date) ?? [];

    const series: Highcharts.SeriesOptionsType[] = metrics
      .map((metric, idx) => {
        const baseId = metric.isPrev && metric.id.endsWith("_prev") ? metric.id.slice(0, -"_prev".length) : metric.id;
        const baseName =
          metric.name == null ? t(metricNameSelector(baseId), { defaultValue: baseId })
          : typeof metric.name === "string" ? metric.name
          : t(metric.name);
        const resolvedName =
          metric.isPrev ? t(($) => $.merchandising["{name} (Previous)"], { name: baseName }) : baseName;
        const chartData =
          data?.map((d, i) => {
            if (
              group === "day" &&
              resolvedName === cellHighlightKey &&
              cellHighlightDates !== null &&
              dates[i] &&
              cellHighlightDates.includes(dates[i])
            ) {
              return { y: metric.accessor(d) ?? null, color: trellisPalette[4] };
            }
            // The color is set explicitly because Highcharts merges updated point options into the old ones, so a
            // highlight that's been removed would otherwise keep its color
            return { y: metric.accessor(d) ?? null, color: metric.color };
          }) ?? [];

        const additionalOptions: {
          fillOpacity?: number;
          opacity?: number;
        } = {};
        const chartType = metric.chartType ?? "area";
        if (chartType === "area") {
          additionalOptions["fillOpacity"] = 0.1;
        }
        if (chartType === "column") {
          additionalOptions["opacity"] = 0.8;
        }
        // tsc needs the assertion to resolve `type` into Highcharts' series union.
        // oxlint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        return {
          id: metric.id,
          custom: { id: metric.id },
          legendIndex: idx,
          data: chartData,
          color: metric.color,
          name: resolvedName,
          type: chartType,
          yAxis: metric.axis,
          visible: selectedMetrics.includes(metric.id),
          tooltip: {
            pointFormatter: function () {
              const formattedValue = formatMetricValue(this.y, {
                format: metric.format as "number" | "currency" | "percent",
                decimalPlaces: metric.decimalPlaces,
                marketPlace,
                compact: true,
              });
              return `<span style="color:${typeof this.color === "string" ? this.color : "#000"}">\u25CF</span> ${this.series.name}: ${formattedValue}<br/>`;
            },
          },
          ...additionalOptions,
        } as Highcharts.SeriesOptionsType;
      })
      .sort((a, b) => {
        if (typeof a.yAxis !== "number" || typeof b.yAxis !== "number") {
          return 0;
        }
        return a.yAxis - b.yAxis;
      });

    const hasData = series.some((s) => "data" in s && s.data && s.data.length! > 0);

    return [hasData, dates, series];
  }, [data, metrics, selectedMetrics, group, cellHighlightKey, cellHighlightDates, marketPlace, t]);

  const getAnnotationsOptions = useGetAnnotations();
  const annotationQuery = useQuery({
    ...getAnnotationsOptions({
      category: category !== "all" ? category : null,
      product: selectedProduct ?? null,
      advertisement: selectedAd ?? null,
      startDate: dates[0] ? moment(dates[0]) : undefined,
      endDate: dates.at(-1) ? moment(dates.at(-1)) : undefined,
      groupby: contextGroup,
    }),
    enabled: !!dates[0] && annotationsEnabled,
    placeholderData: keepPreviousData,
    staleTime: 2 * 60 * 1000,
  });

  const annotationData = useMemo(() => {
    if (!annotationsEnabled || !annotationQuery.data) {
      return [];
    }
    return annotationQuery.data.filter((annotation) =>
      dates.find((c) => moment(c).isSame(moment(annotation.date), contextGroup as unitOfTime.StartOf)),
    );
  }, [annotationQuery.data, annotationsEnabled, dates, contextGroup]);

  // Annotation groups are matched to chart dates by period rather than by position, so a date the API leaves out
  // doesn't shift every later group onto the wrong date
  const getAnnotationGroup = (date: string | null) =>
    date === null ? undefined : (
      annotationData.find((a) => moment(a.date).isSame(moment(date), contextGroup as unitOfTime.StartOf))
    );
  const selectedAnnotations = getAnnotationGroup(selectedDate);

  const getAnnotationMarkerRect = (container: Element) => {
    const chart = chartRef.current?.chart;
    const annotationSeries = chart?.get("annotationAxis") as Highcharts.Series | undefined;
    const point = annotationSeries?.points.find((p) => p.category === selectedDate);
    if (!chart || point?.plotX === undefined || point.plotY === undefined) return null;
    const box = container.getBoundingClientRect();
    return new DOMRect(
      box.left + chart.plotLeft + point.plotX - ANNOTATION_MARKER_RADIUS,
      box.top + chart.plotTop + point.plotY - ANNOTATION_MARKER_RADIUS,
      2 * ANNOTATION_MARKER_RADIUS,
      2 * ANNOTATION_MARKER_RADIUS,
    );
  };

  const chartOptions: Highcharts.Options = {
    title: {
      text: "",
    },
    chart: {
      height: height,
      marginLeft: 15,
      marginRight: 15,
      style: {
        fontFamily: "Poppins",
        fontSize: "15px",
        cursor: "zoom-in",
      },
      events: {
        load: function () {
          chartContainerRef.current = this.container;
        },
      },
    },
    plotOptions: {
      series: {
        pointPadding: 0,
        groupPadding: 0.1,
        borderWidth: 0,
        shadow: false,
        events: {
          legendItemClick: handleLegendChange,
        },
      } satisfies Highcharts.PlotOptions["series"],
    },
    xAxis: {
      crosshair: true,
      categories: dates,
      labels: {
        enabled: true,
        formatter: function () {
          const fmt = group === "month" ? monthYearFmt : monthDayFmt;
          const d = moment(String(this.value), "YYYY-MM-DD", true);
          return d.isValid() ? fmt.format(d.toDate()) : String(this.value);
        },
      },
      tickInterval:
        dates.length <= 12 ? 1
        : size === "small" || mobileStyles ? 4
        : 2,
      plotLines:
        highlightDate ?
          [
            {
              color: "rgba(238, 84, 140, 0.5)",
              fillOpacity: 0.5,
              value: dates.indexOf(highlightDate),
              width: 10,
            },
          ]
        : [],
    },
    yAxis: Array.from(Array(50)).map((): YAxisOptions => ({
      title: {
        text: "",
      },
      visible: false,
      maxPadding: 0,
      min: 0,
      labels: {
        enabled: false,
      },
    })),
    legend: {
      itemStyle: {
        whiteSpace: "normal",
        textAlign: "center",
        fontWeight: "bold",
      },
      itemHiddenStyle: {
        "text-decoration": "none",
        color: "#bbb",
      },
    },
    tooltip: {
      shared: true,
      formatter: function () {
        const points = this.points ?? [];

        // Sort points by legendIndex, keeping annotation series at the end
        const sortedPoints = points.toSorted((a, b) => {
          const aIsAnnotation = a.series.options.id === "annotationAxis";
          const bIsAnnotation = b.series.options.id === "annotationAxis";
          if (aIsAnnotation && !bIsAnnotation) return 1;
          if (!aIsAnnotation && bIsAnnotation) return -1;
          const aIndex = a.series.options.legendIndex ?? 0;
          const bIndex = b.series.options.legendIndex ?? 0;
          return aIndex - bIndex;
        });

        // Header: display the date/category
        const headerKey = sortedPoints[0]?.key;
        const headerMoment = typeof headerKey === "string" ? moment(headerKey, "YYYY-MM-DD", true) : null;
        const headerLabel = headerMoment?.isValid() ? mediumFmt.format(headerMoment.toDate()) : (headerKey ?? "");
        let markup =
          sortedPoints.length ?
            '<span style="font-size: 10px; font-weight: bold; margin-left: 1rem">' + headerLabel + "</span><br/>"
          : "";

        // Delegate to each series' pointFormatter
        for (let index = 0; index < sortedPoints.length; index++) {
          const point = sortedPoints[index];

          if (point) {
            const seriesOptions = point.series.options;

            if ("tooltip" in seriesOptions) {
              const pointFormatter = seriesOptions.tooltip?.pointFormatter;
              if (pointFormatter) {
                markup += pointFormatter.call(point);
              } else {
                // Fallback to default formatting
                markup += `<span style="color:${typeof point.series.color === "string" ? point.series.color : "#000"}">\u25CF</span> ${point.series.name}: ${point.y}<br/>`;
              }
            }
          }
        }

        return markup;
      },
    },
    series: [
      ...series,
      {
        name: "annotationAxis",
        id: "annotationAxis",
        type: "line",
        lineWidth: 0,
        states: { hover: { lineWidthPlus: 0 } },
        showInLegend: false,
        data: (annotationQuery.data ? dates : []).map((date) => {
          const annotationCount = getAnnotationGroup(date)?.annotations.length ?? 0;
          const shouldShow =
            (contextGroup === "day" && (selectedProduct || selectedAd || category)) ||
            supportedOrgLevelAnnotationPaths.includes(pathname) ||
            annotationCount > 0;

          return shouldShow ?
              {
                y: 0,
                className: css({
                  cursor: "pointer",
                }),
                color: annotationCount > 0 || date === selectedDate ? trellisPalette[1] : "transparent",
                marker: {
                  states: {
                    hover: {
                      fillColor: trellisPalette[1],
                    },
                  },
                },
              }
            : null;
        }),
        tooltip: {
          pointFormatter: function () {
            const annotationCount =
              getAnnotationGroup(typeof this.category === "string" ? this.category : null)?.annotations.length ?? 0;

            let markup = `<span style="color:${trellisPalette[1]}">\u25CF</span> ${t(($) => $.merchandising["# of Annotations: {count}"], { count: annotationCount })}<br/>`;

            // Show count of annotations and guide user to click on dots to view and interact with them
            markup += `<br/><span>${t(($) => $.merchandising["Click on the pink dot<br/>below to view or create<br/>annotated events"])}</span>`;

            return markup;
          },
        },
        events: {
          click: (e) => {
            e.preventDefault();

            const dateKey = typeof e.point.category === "string" ? e.point.category : null;
            if (!dateKey) return;

            if (contextGroup === "day" || (getAnnotationGroup(dateKey)?.annotations.length ?? 0) > 0) {
              setSelectedDate(dateKey);
            }
          },
        },
        cursor: "pointer",
        yAxis: 1,
        step: "right",
        visible: true,
        color: trellisPalette[0],
        zIndex: 10, // Highcharts series z-index (above other series)
        marker: {
          enabled: true,
          symbol: "circle",
          radius: ANNOTATION_MARKER_RADIUS,
        },
      },
    ],
  };

  const showOverlay = !hasData || loading;

  return (
    <div
      className={css({
        position: "relative",
      })}
    >
      {showOverlay && (
        <div
          className={css({
            display: "flex",
            zIndex: 1,
            position: "absolute",
            top: "50%",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            color: "#666",
            fontSize: "20px",
            fontWeight: 600,
          })}
        >
          <div
            className={css({
              textAlign: "center",
            })}
          >
            {loading ?
              <span className="fa fa-spinner fa-spin" />
            : t(($) => $.merchandising["No Data available"])}
          </div>
        </div>
      )}
      <div className={css({ position: "relative", width: "100%" })}>
        <Chart options={{ ...chartOptions }} ref={chartRef} />
        {annotationsEnabled && (
          <AnnotationPopover
            triggerRef={chartContainerRef}
            getTargetRect={getAnnotationMarkerRect}
            date={selectedDate === null ? null : (selectedAnnotations?.date ?? selectedDate)}
            onClose={() => setSelectedDate(null)}
            data={selectedAnnotations}
            selectedProduct={selectedProduct}
            marketplace={marketPlace}
            category={category}
            advertisement={selectedAd}
          />
        )}
      </div>
    </div>
  );
};

export default MultiMetricChart;
